import { useEffect, useState } from 'react'
import { httpGet } from '../http.service'

export interface BrandingState {
	brandingLogoUrl: string | null
	organizationDisplayName: string
	organizationWebsiteUrl: string | null
}

const DEFAULT_BRANDING: BrandingState = {
	brandingLogoUrl: null,
	organizationDisplayName: 'POWER PLANT C7',
	organizationWebsiteUrl: null,
}

const BRANDING_STORAGE_KEY = 'edc-portal-branding-cache'

function normalizeBranding(input: unknown): BrandingState {
	if (!input || typeof input !== 'object' || Array.isArray(input)) {
		return DEFAULT_BRANDING
	}
	const raw = input as Record<string, unknown>
	return {
		brandingLogoUrl:
			typeof raw.brandingLogoUrl === 'string' && raw.brandingLogoUrl.trim()
				? raw.brandingLogoUrl
				: null,
		organizationDisplayName:
			typeof raw.organizationDisplayName === 'string' &&
			raw.organizationDisplayName.trim()
				? raw.organizationDisplayName.trim()
				: DEFAULT_BRANDING.organizationDisplayName,
		organizationWebsiteUrl:
			typeof raw.organizationWebsiteUrl === 'string' &&
			raw.organizationWebsiteUrl.trim()
				? raw.organizationWebsiteUrl.trim()
				: null,
	}
}

function readStoredBranding(): BrandingState | null {
	try {
		const raw = localStorage.getItem(BRANDING_STORAGE_KEY)
		if (!raw) return null
		return normalizeBranding(JSON.parse(raw))
	} catch {
		return null
	}
}

function persistBranding(state: BrandingState) {
	try {
		localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(state))
	} catch {
		/* ignore */
	}
}

let cachedBranding: BrandingState | null = readStoredBranding()
let inFlight: Promise<BrandingState> | null = null
const listeners = new Set<(state: BrandingState) => void>()

function notify(state: BrandingState) {
	cachedBranding = state
	persistBranding(state)
	listeners.forEach((listener) => listener(state))
}

async function fetchBranding(): Promise<BrandingState> {
	if (inFlight) return inFlight
	inFlight = (async () => {
		try {
			const response: any = await httpGet('/api/v1/client/app-settings')
			const data = response?.data ?? {}
			let next: BrandingState = normalizeBranding(data)

			// Admin portal fallback: when app-settings branding is empty,
			// pull logo/name from Organization Profile so all themes (including
			// Primersoft) can display dynamic branding instead of static assets.
			if (!next.brandingLogoUrl || !next.organizationDisplayName) {
				try {
					const orgResp: any = await httpGet('/api/v1/admin/org')
					const org = orgResp?.data ?? {}
					next = {
						brandingLogoUrl:
							next.brandingLogoUrl ||
							(typeof org.photoUrl === 'string' && org.photoUrl.trim()
								? org.photoUrl.trim()
								: null),
						organizationDisplayName:
							next.organizationDisplayName ||
							(typeof org.organizationName === 'string' &&
							org.organizationName.trim()
								? org.organizationName.trim()
								: DEFAULT_BRANDING.organizationDisplayName),
						organizationWebsiteUrl:
							next.organizationWebsiteUrl ||
							(typeof org.websiteUrl === 'string' && org.websiteUrl.trim()
								? org.websiteUrl.trim()
								: null),
					}
				} catch {
					// Keep app-settings branding when org endpoint is unavailable.
				}
			}
			notify(next)
			return next
		} catch {
			// Keep last known branding to avoid logo/name flicker on refresh or brief API failures.
			const fallback = cachedBranding ?? DEFAULT_BRANDING
			notify(fallback)
			return fallback
		} finally {
			inFlight = null
		}
	})()
	return inFlight
}

export async function refreshBranding(): Promise<BrandingState> {
	cachedBranding = null
	return fetchBranding()
}

export function useBranding(): BrandingState {
	const [state, setState] = useState<BrandingState>(
		cachedBranding ?? DEFAULT_BRANDING,
	)

	useEffect(() => {
		listeners.add(setState)
		if (!cachedBranding) {
			fetchBranding()
		}
		return () => {
			listeners.delete(setState)
		}
	}, [])

	return state
}

export const BRANDING_DEFAULT = DEFAULT_BRANDING
